import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

export interface TicketRow {
  id: string;
  ticketNumber: number;
  tierName: string;
  priceAtPurchase: number;
  status: string;
  registrationId: string | null;
  reconciled: boolean;
  guestName: string | null;
  mealOptionId: string | null;
  mealOption?: { id: string; name: string } | null;
  memberId: string | null;
  member?: { id: string; firstName: string; lastName: string } | null;
  registration?: {
    guestRecord?: {
      accessibilityNeeds?: string | null;
      seat?: Array<{ seatNumber: number; table: { tableNumber: number } }>;
    } | null;
  } | null;
}

interface MealOption {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface MemberOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface OrderTicketEditorProps {
  orderId: string;
  tickets: TicketRow[];
  onTicketUpdated: () => void;
}

const TICKET_STATUSES = ['Active', 'Cancelled'];

interface EditState {
  registrationId: string;
  status: string;
  guestName: string;
  mealOptionId: string;
  memberId: string;
}

const styles: Record<string, React.CSSProperties> = {
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.8rem',
  },
  th: {
    padding: '0.4rem 0.6rem',
    textAlign: 'left' as const,
    fontWeight: 600,
    color: '#6b7280',
    fontSize: '0.7rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb',
  },
  td: {
    padding: '0.4rem 0.6rem',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    verticalAlign: 'middle' as const,
  },
  input: {
    padding: '0.25rem 0.4rem',
    fontSize: '0.8rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    width: '100%',
    maxWidth: '160px',
    boxSizing: 'border-box' as const,
  },
  select: {
    padding: '0.25rem 0.4rem',
    fontSize: '0.8rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  editButton: {
    padding: '0.2rem 0.5rem',
    fontSize: '0.7rem',
    fontWeight: 500,
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#fff',
    color: '#374151',
    marginRight: '0.3rem',
  },
  saveButton: {
    padding: '0.2rem 0.5rem',
    fontSize: '0.7rem',
    fontWeight: 500,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#0f2d1f',
    color: '#fff',
    marginRight: '0.3rem',
  },
  cancelButton: {
    padding: '0.2rem 0.5rem',
    fontSize: '0.7rem',
    fontWeight: 500,
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#fff',
    color: '#374151',
  },
  disabledButton: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    color: '#d32f2f',
    fontSize: '0.75rem',
    marginTop: '0.25rem',
  },
  emptyState: {
    padding: '1rem',
    textAlign: 'center' as const,
    color: '#6b7280',
    fontSize: '0.8rem',
  },
};

export function OrderTicketEditor({
  orderId,
  tickets,
  onTicketUpdated,
}: OrderTicketEditorProps) {
  const [editingTicketId, setEditingTicketId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({
    registrationId: '',
    status: '',
    guestName: '',
    mealOptionId: '',
    memberId: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mealOptions, setMealOptions] = useState<MealOption[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);

  useEffect(() => {
    async function fetchMealOptions() {
      try {
        const options = await apiClient<MealOption[]>('/legacy-ball/meal-options');
        setMealOptions(options.filter((o) => o.active));
      } catch {
        // Silently fail — dropdown will be empty
      }
    }
    fetchMealOptions();
  }, []);

  useEffect(() => {
    async function fetchMembers() {
      try {
        const result = await apiClient<MemberOption[]>('/members');
        setMembers(result);
      } catch {
        // Silently fail — dropdown will be empty
      }
    }
    fetchMembers();
  }, []);

  const handleEdit = (ticket: TicketRow) => {
    setEditingTicketId(ticket.id);
    setEditState({
      registrationId: ticket.registrationId || '',
      status: ticket.status,
      guestName: ticket.guestName || '',
      mealOptionId: ticket.mealOptionId || '',
      memberId: ticket.memberId || '',
    });
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingTicketId(null);
    setEditState({ registrationId: '', status: '', guestName: '', mealOptionId: '', memberId: '' });
    setError('');
  };

  const handleSave = async (ticketId: string) => {
    setSaving(true);
    setError('');
    try {
      await apiClient(
        `/legacy-ball/orders/${orderId}/tickets/${ticketId}`,
        {
          method: 'PATCH',
          body: {
            registrationId: editState.registrationId || null,
            status: editState.status,
            guestName: editState.guestName || null,
            mealOptionId: editState.mealOptionId || null,
            memberId: editState.memberId || null,
          },
        },
      );
      setEditingTicketId(null);
      onTicketUpdated();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Failed to update ticket.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (tickets.length === 0) {
    return <div style={styles.emptyState}>No tickets found for this order.</div>;
  }

  return (
    <div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, width: '24px', textAlign: 'center', padding: '0.4rem 2px' }}>♿</th>
            <th style={styles.th}>Ticket #</th>
            <th style={styles.th}>Guest Name</th>
            <th style={styles.th}>Table</th>
            <th style={styles.th}>Seat</th>
            <th style={styles.th}>Member</th>
            <th style={styles.th}>Meal</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Reconciled</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((ticket) => {
            const isEditing = editingTicketId === ticket.id;
            return (
              <tr key={ticket.id}>
                <td style={{ ...styles.td, width: '24px', textAlign: 'center', padding: '0.4rem 2px' }}>
                  {ticket.registration?.guestRecord?.accessibilityNeeds ? <span title={ticket.registration.guestRecord.accessibilityNeeds} style={{ fontSize: '0.85rem' }}>♿</span> : ''}
                </td>
                <td style={styles.td}>
                  TKT-{String(ticket.ticketNumber).padStart(3, '0')}
                </td>
                <td style={styles.td}>
                  {isEditing ? (
                    <input
                      style={styles.input}
                      type="text"
                      value={editState.guestName}
                      onChange={(e) =>
                        setEditState((prev) => ({
                          ...prev,
                          guestName: e.target.value,
                        }))
                      }
                      disabled={saving}
                      placeholder="Guest name"
                      aria-label={`Guest name for ticket ${ticket.ticketNumber}`}
                    />
                  ) : (
                    ticket.guestName || '—'
                  )}
                </td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  {ticket.registration?.guestRecord?.seat?.[0]?.table?.tableNumber ?? '—'}
                </td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  {ticket.registration?.guestRecord?.seat?.[0]?.seatNumber ?? '—'}
                </td>
                <td style={styles.td}>
                  {isEditing ? (
                    <select
                      style={styles.select}
                      value={editState.memberId}
                      onChange={(e) => {
                        const selectedMember = members.find(m => m.id === e.target.value);
                        setEditState((prev) => ({
                          ...prev,
                          memberId: e.target.value,
                          guestName: selectedMember ? `${selectedMember.firstName} ${selectedMember.lastName}` : prev.guestName,
                        }));
                      }}
                      disabled={saving}
                      aria-label={`Member for ticket ${ticket.ticketNumber}`}
                    >
                      <option value="">— Select Member (optional) —</option>
                      {members.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.firstName} {m.lastName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    ticket.member ? `${ticket.member.firstName} ${ticket.member.lastName}` : '—'
                  )}
                </td>
                <td style={styles.td}>
                  {isEditing ? (
                    <select
                      style={styles.select}
                      value={editState.mealOptionId}
                      onChange={(e) =>
                        setEditState((prev) => ({
                          ...prev,
                          mealOptionId: e.target.value,
                        }))
                      }
                      disabled={saving}
                      aria-label={`Meal for ticket ${ticket.ticketNumber}`}
                    >
                      <option value="">No meal selected</option>
                      {mealOptions.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    ticket.mealOption?.name || '—'
                  )}
                </td>
                <td style={styles.td}>
                  {isEditing ? (
                    <select
                      style={styles.select}
                      value={editState.status}
                      onChange={(e) =>
                        setEditState((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      disabled={saving}
                      aria-label={`Status for ticket ${ticket.ticketNumber}`}
                    >
                      {TICKET_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  ) : (
                    ticket.status
                  )}
                </td>
                <td style={styles.td}>
                  {ticket.reconciled ? 'Yes' : 'No'}
                </td>
                <td style={styles.td}>
                  {isEditing ? (
                    <>
                      <button
                        style={{
                          ...styles.saveButton,
                          ...(saving ? styles.disabledButton : {}),
                        }}
                        onClick={() => handleSave(ticket.id)}
                        disabled={saving}
                        aria-label={`Save ticket ${ticket.ticketNumber}`}
                      >
                        Save
                      </button>
                      <button
                        style={{
                          ...styles.cancelButton,
                          ...(saving ? styles.disabledButton : {}),
                        }}
                        onClick={handleCancelEdit}
                        disabled={saving}
                        aria-label={`Cancel editing ticket ${ticket.ticketNumber}`}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      style={styles.editButton}
                      onClick={() => handleEdit(ticket)}
                      aria-label={`Edit ticket ${ticket.ticketNumber}`}
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}
